import React from 'react';
import { motion } from 'framer-motion';
import { SlidersHorizontal, Users, DollarSign } from 'lucide-react';
interface RoomFiltersProps {
  selectedType: string | null;
  onTypeSelect: (type: string | null) => void;
  capacityRange: [number, number];
  onCapacityChange: (range: [number, number]) => void;
  priceRange: [number, number];
  onPriceChange: (range: [number, number]) => void;
}
export function RoomFilters({
  selectedType,
  onTypeSelect,
  capacityRange,
  onCapacityChange,
  priceRange,
  onPriceChange
}: RoomFiltersProps) {
  const roomTypes = [{
    id: 'all',
    name: 'All Rooms',
    count: 12
  }, {
    id: 'vip-suite',
    name: 'VIP Suites',
    count: 4
  }, {
    id: 'private-lounge',
    name: 'Private Lounges',
    count: 5
  }, {
    id: 'event-space',
    name: 'Event Spaces',
    count: 3
  }];
  return <div className="space-y-6" data-id="element-475">
      {/* Header */}
      <div className="flex items-center gap-3 pb-6 border-b border-white/10" data-id="element-476">
        <div className="w-10 h-10 bg-[#DDA247]/10 rounded-lg flex items-center justify-center" data-id="element-477">
          <SlidersHorizontal className="w-5 h-5 text-[#DDA247]" data-id="element-478" />
        </div>
        <h2 className="text-lg font-bold text-white uppercase tracking-wide" data-id="element-479">
          Filters
        </h2>
      </div>

      {/* Room Types */}
      <div data-id="element-480">
        <label className="block text-xs font-medium text-gray-400 uppercase tracking-widest mb-4" data-id="element-481">
          Room Type
        </label>
        <div className="space-y-2" data-id="element-482">
          {roomTypes.map(type => <motion.button key={type.id} onClick={() => onTypeSelect(selectedType === type.id ? null : type.id)} whileHover={{
          x: 4
        }} className={`w-full flex items-center justify-between p-3 rounded-lg transition-all ${selectedType === type.id ? 'bg-[#DDA247]/10 border border-[#DDA247]' : 'bg-[#1A2228] border border-white/5 hover:border-white/20'}`} data-id="element-483">
              <span className={`text-sm font-medium ${selectedType === type.id ? 'text-[#DDA247]' : 'text-white'}`} data-id="element-484">
                {type.name}
              </span>
              <span className="text-xs text-gray-500" data-id="element-485">({type.count})</span>
            </motion.button>)}
        </div>
      </div>

      {/* Capacity Range */}
      <div data-id="element-486">
        <label className="block text-xs font-medium text-gray-400 uppercase tracking-widest mb-4" data-id="element-487">
          <Users className="w-3 h-3 inline mr-2" data-id="element-488" />
          Guest Capacity
        </label>
        <div className="space-y-4" data-id="element-489">
          <div className="flex items-center justify-between text-sm" data-id="element-490">
            <span className="text-gray-400" data-id="element-491">
              Min: {capacityRange[0]} guests
            </span>
            <span className="text-gray-400" data-id="element-492">
              Max: {capacityRange[1]} guests
            </span>
          </div>
          <input type="range" min="2" max="100" value={capacityRange[1]} onChange={e => onCapacityChange([capacityRange[0], parseInt(e.target.value)])} className="w-full h-2 bg-[#1A2228] rounded-lg appearance-none cursor-pointer accent-[#DDA247]" data-id="element-493" />
        </div>
      </div>

      {/* Price Range */}
      <div data-id="element-494">
        <label className="block text-xs font-medium text-gray-400 uppercase tracking-widest mb-4" data-id="element-495">
          <DollarSign className="w-3 h-3 inline mr-2" data-id="element-496" />
          Price Range
        </label>
        <div className="space-y-4" data-id="element-497">
          <div className="flex items-center justify-between text-sm" data-id="element-498">
            <span className="text-gray-400" data-id="element-499">
              ${priceRange[0].toLocaleString()}
            </span>
            <span className="text-gray-400" data-id="element-500">
              ${priceRange[1].toLocaleString()}
            </span>
          </div>
          <input type="range" min="500" max="10000" step="500" value={priceRange[1]} onChange={e => onPriceChange([priceRange[0], parseInt(e.target.value)])} className="w-full h-2 bg-[#1A2228] rounded-lg appearance-none cursor-pointer accent-[#DDA247]" data-id="element-501" />
        </div>
      </div>

      {/* Amenities */}
      <div data-id="element-502">
        <label className="block text-xs font-medium text-gray-400 uppercase tracking-widest mb-4" data-id="element-503">
          Amenities
        </label>
        <div className="space-y-2" data-id="element-504">
          {['WiFi', 'Sound System', 'Smart TV', 'Mini Bar', 'Private Bathroom', 'Balcony'].map(amenity => <label key={amenity} className="flex items-center gap-3 p-2 rounded-lg hover:bg-[#1A2228]/50 cursor-pointer group" data-id="element-505">
              <input type="checkbox" className="w-4 h-4 rounded border-white/20 bg-[#1A2228] text-[#DDA247] focus:ring-[#DDA247] focus:ring-offset-0" data-id="element-506" />
              <span className="text-sm text-gray-400 group-hover:text-white transition-colors" data-id="element-507">
                {amenity}
              </span>
            </label>)}
        </div>
      </div>

      {/* Reset Button */}
      <motion.button whileHover={{
      scale: 1.02
    }} whileTap={{
      scale: 0.98
    }} onClick={() => {
      onTypeSelect(null);
      onCapacityChange([2, 100]);
      onPriceChange([500, 10000]);
    }} className="w-full py-3 bg-[#1A2228] border border-white/10 rounded-lg text-sm text-gray-400 hover:text-white hover:border-white/30 transition-all uppercase tracking-wider font-medium" data-id="element-508">
        Reset Filters
      </motion.button>
    </div>;
}